import {
  Flex,
  Icon,
  useToast,
  chakra,
  Box,
  Progress,
  Spinner,
} from "@chakra-ui/react";
import {
  IoMdAlert,
} from "react-icons/io";
import { useState, useEffect } from "react";
import { CheckIcon, CircleCheck, CircleX, XCircle, XIcon } from "lucide-react";

let activeToastId = null;

export const useCustomNotification = () => {
  const toast = useToast();

  return ({
    message = "",
    description = "",
    duration = 5,
    type = "info",
  }) => {
    if (activeToastId) {
      toast.close(activeToastId);
    }

    const config = {
      status: type,
      position: "top-right",
      duration: duration * 773,
      isClosable: true,
      render: ({ onClose }) => (
        <Box
          onClick={() => {
            onClose();
            activeToastId = null;
          }}
        >
          <StatusSwitcher data={{ type, message, description, duration }} />
        </Box>
      ),
    };

    activeToastId = toast(config);
  };
};

// Main component switcher
const StatusSwitcher = ({ data }) => {
  const { type } = data;
  switch (type) {
    case "info":
      return <Info data={data} />;
    case "warning":
      return <Warning data={data} />;
    case "success":
      return <Success data={data} />;
    case "error":
      return <Error data={data} />;
    case "loading":
      return <Loading data={data} />;
    default:
      return null;
  }
};

// Shared Layout
const ToastLayout = ({ icon, color, message, description, duration, colorScheme }) => (
  <Flex
    direction="column"
    maxW="sm"
    w="full"
    bg="white"
    boxShadow="lg"
    rounded="lg"
    overflow="hidden"
  >
    <Flex align="center" gap={3} px={4} py={3}>
      <Icon as={icon} boxSize={6} color="white" bg={color} borderRadius="full" p={1} />
      <Box>
        <chakra.span color={color} fontWeight="bold" fontSize="sm">
          {message}
        </chakra.span>
        <chakra.p color="gray.600" fontSize="sm">
          {description}
        </chakra.p>
      </Box>
    </Flex>
    <ProgressBar duration={duration} colorScheme={colorScheme} />
  </Flex>
);

// Success
export const Success = ({ data }) => {
  const { message = "Success", description, duration } = data;
  return (
    <ToastLayout
      icon={CheckIcon}
      color="green.500"
      colorScheme="green"
      message={message}
      description={description}
      duration={duration}
    />
  );
};

// Error
export const Error = ({ data }) => {
  const { message = "Error", description, duration } = data;
  return (
    <ToastLayout
      icon={XIcon}
      color="red.500"
      colorScheme="red"
      message={message}
      description={description}
      duration={duration}
    />
  );
};

// Warning
export const Warning = ({ data }) => {
  const { message = "Warning", description, duration } = data;
  return (
    <ToastLayout
      icon={IoMdAlert}
      color="yellow.500"
      colorScheme="yellow"
      message={message}
      description={description}
      duration={duration}
    />
  );
};

// Info
export const Info = ({ data }) => {
  const { message = "Info", description, duration } = data;
  return (
    <ToastLayout
      icon={IoMdAlert}
      color="blue.400"
      colorScheme="blue"
      message={message}
      description={description}
      duration={duration}
    />
  );
};

// Loading (Styled a bit differently)
export const Loading = ({ data }) => {
  const { description, duration } = data;

  return (
    <Flex
      direction="column"
      maxW="sm"
      w="full"
      bg="white"
      shadow="md"
      rounded="lg"
      overflow="hidden"
    >
      <Flex align="center" gap={3} px={4} py={3}>
        <Box
          bg="gray.600"
          borderRadius="full"
          p={1.5}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="sm" color="white" />
        </Box>
        <chakra.p color="gray.700" fontSize="sm">
          {description || "Loading..."}
        </chakra.p>
      </Flex>
      <ProgressBar duration={duration} colorScheme="gray" />
    </Flex>
  );
};

// Progress Bar
const ProgressBar = ({ duration, colorScheme }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => (prev > 0 ? prev - 100 / (duration * 10) : 0));
    }, 100);
    return () => clearInterval(interval);
  }, [duration]);

  return (
    <Progress
      value={progress}
      size="xs"
      colorScheme={colorScheme}
      borderRadius="lg"
      hasStripe
    />
  );
};
